import React, { useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
//mui imports====
import { Box } from '@mui/system';
import useStyles from './styles';
import { Chip, Grid, TextField, Typography } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
//===============

/**
 * @name ArrayInputConfigv2 component.
 * @description This component is used to render an input configuration for an array. It allows adding, updating, and deleting elements in the array
 */
const ArrayInputConfigv2 = ({
	keyData,
	object,
	updateObject,
	isObjectEditor = true,
	directValue,
	updateDirectValue,
	label,
	inputContainerStyle,
	inputGridStyle,
	entireArrayEditable,
	isMandatory = false,
	getItemStatus
}) => {
	const classes = useStyles();
	const [vastTagArrayClipboardValue, setVastTagArrayClipboardValue] = useState(null);
	const itemIds = useRef([]);
	const nextItemId = useRef(0);
	// eslint-disable-next-line
	const inputArray = (isObjectEditor ? object[keyData] : directValue) || [];
	const displayLabel = isMandatory ? `${label} *` : label;
	const createItemId = () => `${keyData}-${nextItemId.current++}`;

	while (itemIds.current.length < inputArray.length) {
		itemIds.current.push(createItemId());
	}
	if (itemIds.current.length > inputArray.length) {
		itemIds.current.splice(inputArray.length);
	}

	/**
	 * @name addNewElement
	 * @description This function can be used to add a new element to array, with two scenarios if isObjectEditor is true or false
	 */
	const addNewElement = () => {
		const deepCloneArray = structuredClone(inputArray);
		const updatedArray = [...deepCloneArray, ''];
		itemIds.current.push(createItemId());
		if (isObjectEditor && object) {
			const rawObject = structuredClone(object);
			updateObject({ ...rawObject, [keyData]: updatedArray });
		} else {
			updateDirectValue(updatedArray);
		}
	};

	/**
	 *  @name addElementAbove
	 * @description This function can be used to add a new element above the particular array element, with two scenarios if the isObjectEditor is true or false
	 * @param {number} index - it is the index of the particular array element above which the new element is to be added
	 */
	const addElementAbove = (index) => {
		const deepCloneArray = structuredClone(inputArray);
		const updatedArray = [...deepCloneArray.slice(0, index), '', ...deepCloneArray.slice(index)];
		itemIds.current.splice(index, 0, createItemId());
		if (isObjectEditor && object) {
			const rawObject = structuredClone(object);
			updateObject({ ...rawObject, [keyData]: updatedArray });
		} else if (directValue) {
			updateDirectValue(updatedArray);
		} else
			toast.error("Couldn't find inputArray in rawConfig!", {
				position: toast.POSITION.BOTTOM_RIGHT
			});
	};

	/**
	 * @name addElementBelow
	 * @description This function can be used to add a new element below the particular array element, with two scenarios if the isObjectEditor is true or false
	 * @param {number} index - it is the index of the particular array element below which the new element is to be added
	 */
	const addElementBelow = (index) => {
		const deepCloneArray = structuredClone(inputArray);
		const updatedArray = [
			...deepCloneArray.slice(0, index + 1),
			'',
			...deepCloneArray.slice(index + 1)
		];
		itemIds.current.splice(index + 1, 0, createItemId());
		if (isObjectEditor && object) {
			const rawObject = structuredClone(object);
			updateObject({ ...rawObject, [keyData]: updatedArray });
		} else if (directValue) {
			updateDirectValue(updatedArray);
		} else
			toast.error("Couldn't find inputArray in rawConfig!", {
				position: toast.POSITION.BOTTOM_RIGHT
			});
	};

	/**
	 * @name updateArrayElement
	 * @description This function can be used to update an element in the array, with two scenarios if the isObjectEditor is true or false
	 * 1. true : updated array element with the rawConfig functionality and passes value to call back function
	 * 2. false : updated array element with  direct value functionality and passes it to the callback function
	 * @param {number} index - it is the index of the particular array element which is to be updated
	 * @param {string} updatedValue - it is the updated value  which is to be updated over the particular array element
	 */
	const updateArrayElement = (index, upatedValue) => {
		let updatedArray;
		if (isObjectEditor && object && object[keyData] && Array.isArray(object[keyData])) {
			const rawObject = structuredClone(object);
			updatedArray = structuredClone(object[keyData]);
			updatedArray[index] = upatedValue;
			updateObject({ ...rawObject, [keyData]: updatedArray });
		} else if (directValue) {
			updatedArray = structuredClone(directValue);
			updatedArray[index] = upatedValue;
			updateDirectValue(updatedArray);
		} else
			toast.error("Couldn't find inputArray in rawConfig!", {
				position: toast.POSITION.BOTTOM_RIGHT
			});
	};

	/**
	 * @name deleteArrayElement
	 * @description This function can be used to delete an element from the array, with two scenarios if the isObjectEditor is true or false
	 * 1. true : deletes an array element with the rawConfig functionality and passes value to call back function
	 * 2. false : deletes an array element with  direct value functionality and passes it to the callback function
	 * @param {number} idx - it is the id of the particular array element which is to be deleted
	 */
	const deleteArrayElement = (idx) => {
		let updatedArray;
		if (isObjectEditor && object && object[keyData] && Array.isArray(object[keyData])) {
			const rawObject = structuredClone(object);
			updatedArray = structuredClone(object[keyData]);
			updatedArray = updatedArray.filter((data, id) => id !== idx);
			itemIds.current.splice(idx, 1);

			updateObject({ ...rawObject, [keyData]: updatedArray });
		} else if (directValue) {
			updatedArray = structuredClone(directValue);
			updatedArray = updatedArray.filter((data, id) => id !== idx);
			itemIds.current.splice(idx, 1);
			updateDirectValue(updatedArray);
		} else
			toast.error("Couldn't find inputArray in rawConfig!", {
				position: toast.POSITION.BOTTOM_RIGHT
			});
	};

	/**
	 * @name handleDragEnd Handles the drag end event for reordering items in the input array.
	 * @param {object} result - The result object containing information about the drag event.
	 * @description This function can be used to reorder the array elements, with two scenarios if the isObjectEditor is true or false
	 */
	const handleDragEnd = (result) => {
		if (!result.destination) return;

		const itemsCopy = Array.from(inputArray);
		const [reorderedItem] = itemsCopy.splice(result.source.index, 1);
		const [reorderedId] = itemIds.current.splice(result.source.index, 1);
		itemsCopy.splice(result.destination.index, 0, reorderedItem);
		itemIds.current.splice(result.destination.index, 0, reorderedId);

		if (isObjectEditor && object) {
			const rawObject = structuredClone(object);
			updateObject({ ...rawObject, [keyData]: itemsCopy });
		} else if (directValue) {
			updateDirectValue(itemsCopy);
		} else
			toast.error("Couldn't find inputArray in rawConfig!", {
				position: toast.POSITION.BOTTOM_RIGHT
			});
	};

	// This function is used to copy the VastTag array to the clipboard
	function copyVastTagArrayToClipboard() {
		if (inputArray.length) {
			setVastTagArrayClipboardValue(inputArray);
			toast.success('VastTag Array copied to clipboard!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		} else {
			toast.error('VastTag Array is empty!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}
	}

	// This function is used to paste the VastTag array from the clipboard
	function pasteVastTagArrayFromClipboard() {
		if (vastTagArrayClipboardValue && Array.isArray(vastTagArrayClipboardValue)) {
			itemIds.current = vastTagArrayClipboardValue.map(() => createItemId());
			if (isObjectEditor && object) {
				const rawObject = structuredClone(object);
				updateObject({ ...rawObject, [keyData]: vastTagArrayClipboardValue });
			} else {
				updateDirectValue(vastTagArrayClipboardValue);
			}
			toast.success('VastTag Array pasted from clipboard!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		} else {
			toast.error('No VastTag array found in clipboard!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}
	}

	return (
		<>
			<Box sx={{ margin: '1.5rem 0', display: 'flex' }} spacing={4}>
				<Typography fontWeight={600} variant="h5">
					{displayLabel}
				</Typography>
				{inputArray.length === 0 && (
					<AddCircleOutlineOutlinedIcon
						color="primary"
						fontSize="large"
						className={classes.icon}
						onClick={addNewElement}
						style={{ marginLeft: '1rem' }}
					/>
				)}
				<ContentCopyIcon
					color="primary"
					style={{ marginLeft: '2rem', marginTop: '0.5rem' }}
					className={classes.icon}
					onClick={() => {
						copyVastTagArrayToClipboard();
					}}
				/>
				<ContentPasteIcon
					color="primary"
					style={{ marginLeft: '2rem', marginTop: '0.5rem' }}
					className={classes.icon}
					onClick={() => {
						pasteVastTagArrayFromClipboard();
					}}
				/>
			</Box>
			<Grid style={inputContainerStyle || {}}>
				<DragDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId={`array-input-${keyData}`}>
						{(provided) => (
							<div {...provided.droppableProps} ref={provided.innerRef}>
								{inputArray.map((item, index) => {
									const itemId = itemIds.current[index];

									return (
										<Draggable key={itemId} draggableId={itemId} index={index}>
											{(provided) => (
												<div
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
												>
													<SingleArrayElement
														key={index}
														index={index}
														arrayElement={item}
														updateArrayElement={updateArrayElement}
														deleteArrayElement={deleteArrayElement}
														editable={entireArrayEditable}
														inputGridStyle={inputGridStyle || {}}
														addElementAbove={addElementAbove}
														addElementBelow={addElementBelow}
														getItemStatus={getItemStatus}
													/>
												</div>
											)}
										</Draggable>
									);
								})}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			</Grid>
		</>
	);
};

const SingleArrayElement = React.memo(
	({
		index,
		arrayElement,
		updateArrayElement,
		deleteArrayElement,
		editable,
		inputGridStyle,
		addElementAbove,
		addElementBelow,
		getItemStatus
	}) => {
		const classes = useStyles();
		const itemStatus = getItemStatus?.(arrayElement);
		const isValidItem = itemStatus?.type === 'success';
		const isInvalidItem = itemStatus?.type === 'error';
		return (
			<>
				{index === 0 && (
					<Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
						<AddCircleOutlineOutlinedIcon
							color="primary"
							fontSize="small"
							className={classes.icon}
							onClick={() => addElementAbove(index)}
							style={{ margin: '0 auto' }}
						/>
					</Grid>
				)}
				<Grid item xs={4} style={inputGridStyle}>
					<DragIndicatorIcon
						color="primary"
						style={{ marginTop: '2.5rem', marginRight: '0.5rem' }}
					/>
					{itemStatus ? (
						<Box
							style={{
								width: '100%',
								border: `1px solid ${isValidItem ? '#2e7d32' : '#d32f2f'}`,
								borderRadius: '6px',
								backgroundColor: isValidItem ? '#fbfffc' : '#fffafa',
								padding: '0.75rem 1rem'
							}}
						>
							<Box
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '0.75rem',
									marginBottom: '0.6rem'
								}}
							>
								<Chip
									color={isValidItem ? 'success' : 'error'}
									icon={isValidItem ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
									label={itemStatus.label}
									size="small"
								/>
								<Typography
									variant="body2"
									style={{ color: isValidItem ? '#2e7d32' : '#d32f2f', fontWeight: 500 }}
								>
									{itemStatus.details}
								</Typography>
							</Box>
							<TextField
								id="outlined-size-normal-arrayElement"
								style={{ width: '100%' }}
								value={arrayElement || ''}
								error={isInvalidItem}
								onChange={(e) => {
									updateArrayElement(index, e.target.value);
								}}
								disabled={editable === false}
							/>
						</Box>
					) : (
						<TextField
							id="outlined-size-normal-arrayElement"
							style={{ width: '100%', margin: '1rem 0' }}
							value={arrayElement || ''}
							onChange={(e) => {
								updateArrayElement(index, e.target.value);
							}}
							disabled={editable === false}
						/>
					)}

					<Box style={{ display: 'flex', alignItems: 'center', marginLeft: '.5rem' }}>
						<DeleteOutlinedIcon
							color="primary"
							onClick={() => deleteArrayElement(index)}
							className={classes.icon}
						/>
					</Box>
				</Grid>

				<Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
					<AddCircleOutlineOutlinedIcon
						color="primary"
						fontSize="small"
						className={classes.icon}
						onClick={() => addElementBelow(index)}
						style={{ margin: '0 auto' }}
					/>
				</Grid>
			</>
		);
	}
);
export default ArrayInputConfigv2;
